import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Loader2, CheckCircle2, Mail, Check } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

type Source = "footer" | "newsletter_page" | "post_cta";
type Variant = "inline" | "hero";

interface NewsletterSignupProps {
  source: Source;
  variant?: Variant;
  className?: string;
}

const INTEREST_OPTIONS = [
  "ai_strategy",
  "governance_compliance",
  "ai_literacy_training",
  "case_studies",
  "events_workshops",
  "tools_playbooks",
] as const;

const ORG_SIZE_OPTIONS = [
  "solo",
  "2-10",
  "11-50",
  "51-200",
  "201-1000",
  "1000+",
] as const;

const emailSchema = z.string().trim().min(1).max(255).email();

/** Soft, branded newsletter signup. Inserts into newsletter_subscribers. */
const NewsletterSignup = ({ source, variant = "inline", className }: NewsletterSignupProps) => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [organization, setOrganization] = useState("");
  const [orgSize, setOrgSize] = useState<string>("");
  const [interests, setInterests] = useState<string[]>([]);
  const [honeypot, setHoneypot] = useState("");
  const [state, setState] = useState<"idle" | "submitting" | "success">("idle");
  const [error, setError] = useState<string | null>(null);
  const mountedAt = useRef<number>(Date.now());

  const isHero = variant === "hero";

  const toggleInterest = (value: string) => {
    setInterests((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (honeypot) return;
    if (Date.now() - mountedAt.current < 800) return;

    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setError(t("newsletter.form.invalidEmail"));
      return;
    }

    setState("submitting");

    const payload: Record<string, unknown> = {
      email: parsed.data,
      source,
      locale: (i18n.language || "en").startsWith("sv") ? "sv" : "en",
    };
    if (isHero) {
      const trim = (s: string, max: number) => s.trim().slice(0, max);
      if (firstName.trim()) payload.first_name = trim(firstName, 80);
      if (roleTitle.trim()) payload.role_title = trim(roleTitle, 120);
      if (organization.trim()) payload.organization = trim(organization, 120);
      if (orgSize && ORG_SIZE_OPTIONS.includes(orgSize as typeof ORG_SIZE_OPTIONS[number])) {
        payload.organization_size = orgSize;
      }
      if (interests.length > 0) payload.interests = interests;
    }

    const { data, error: invokeError } = await supabase.functions.invoke(
      "subscribe-newsletter",
      { body: payload },
    );

    if (invokeError || (data && (data as { error?: string }).error)) {
      setState("idle");
      setError(t("newsletter.form.errorGeneric"));
      return;
    }

    setState("success");
  };

  if (state === "success") {
    return (
      <div
        className={cn(
          "flex items-start gap-2 text-sm",
          isHero ? "justify-center" : "",
          className
        )}
      >
        <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" aria-hidden />
        <p className="text-foreground">{t("newsletter.form.success")}</p>
      </div>
    );
  }

  // Inline (email-only) variant — used on news article CTA
  if (!isHero) {
    return (
      <form
        onSubmit={handleSubmit}
        className={cn("w-full", className)}
        noValidate
      >
        <input
          type="text"
          name="hp_company"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          className="hidden"
          aria-hidden
        />
        <div className="flex gap-2 flex-col sm:flex-row">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden />
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("newsletter.form.emailPlaceholder")}
              aria-label={t("newsletter.form.emailPlaceholder")}
              disabled={state === "submitting"}
              className="pl-9"
            />
          </div>
          <Button type="submit" disabled={state === "submitting"}>
            {state === "submitting" ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("newsletter.form.submitting")}
              </>
            ) : (
              t("newsletter.form.submit")
            )}
          </Button>
        </div>
        {error && (
          <p className="mt-2 text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
      </form>
    );
  }

  // Hero variant — full signup card with optional profile fields
  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        "w-full max-w-xl mx-auto rounded-2xl border border-border bg-card p-6 md:p-8 space-y-5 text-left",
        className
      )}
      noValidate
    >
      <input
        type="text"
        name="hp_company"
        tabIndex={-1}
        autoComplete="off"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        className="hidden"
        aria-hidden
      />

      {/* Email (required) */}
      <div className="space-y-1.5">
        <Label htmlFor="nl-email">{t("newsletter.form.emailLabel")}</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden />
          <Input
            id="nl-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("newsletter.form.emailPlaceholder")}
            disabled={state === "submitting"}
            className="pl-9 h-11"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="nl-firstname">
            {t("newsletter.form.firstNameLabel")}{" "}
            <span className="text-muted-foreground font-normal">
              {t("newsletter.form.optional")}
            </span>
          </Label>
          <Input
            id="nl-firstname"
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            maxLength={80}
            disabled={state === "submitting"}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="nl-role">
            {t("newsletter.form.roleTitleLabel")}{" "}
            <span className="text-muted-foreground font-normal">
              {t("newsletter.form.optional")}
            </span>
          </Label>
          <Input
            id="nl-role"
            type="text"
            value={roleTitle}
            onChange={(e) => setRoleTitle(e.target.value)}
            maxLength={120}
            placeholder={t("newsletter.form.roleTitlePlaceholder")}
            disabled={state === "submitting"}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="nl-org">
            {t("newsletter.form.organizationLabel")}{" "}
            <span className="text-muted-foreground font-normal">
              {t("newsletter.form.optional")}
            </span>
          </Label>
          <Input
            id="nl-org"
            type="text"
            autoComplete="organization"
            value={organization}
            onChange={(e) => setOrganization(e.target.value)}
            maxLength={120}
            disabled={state === "submitting"}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="nl-size">
            {t("newsletter.form.organizationSizeLabel")}{" "}
            <span className="text-muted-foreground font-normal">
              {t("newsletter.form.optional")}
            </span>
          </Label>
          <Select value={orgSize} onValueChange={setOrgSize} disabled={state === "submitting"}>
            <SelectTrigger id="nl-size">
              <SelectValue placeholder={t("newsletter.form.organizationSizePlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {ORG_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={size}>
                  {t(`newsletter.form.organizationSizeOptions.${size}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>
          {t("newsletter.form.interestsLabel")}{" "}
          <span className="text-muted-foreground font-normal">
            {t("newsletter.form.optional")}
          </span>
        </Label>
        <div className="flex flex-wrap gap-2">
          {INTEREST_OPTIONS.map((key) => {
            const selected = interests.includes(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggleInterest(key)}
                disabled={state === "submitting"}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm border transition-colors",
                  selected
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:text-foreground hover:border-foreground/40"
                )}
                aria-pressed={selected}
              >
                {selected && <Check className="w-3.5 h-3.5" />}
                {t(`newsletter.form.interestsOptions.${key}`)}
              </button>
            );
          })}
        </div>
      </div>

      <Button
        type="submit"
        disabled={state === "submitting"}
        className="w-full h-11 text-base"
      >
        {state === "submitting" ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t("newsletter.form.submitting")}
          </>
        ) : (
          t("newsletter.form.submit")
        )}
      </Button>

      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </form>
  );
};

export default NewsletterSignup;
